import {
  Directive,
  ElementRef,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  Input,
  SimpleChanges,
  forwardRef,
  Renderer2
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
* Replacement for angular2-materialize's materialize="material_select"
* Compatible with Materialize CSS v0.100.2 (jQuery plugin API)
*
* Implements ControlValueAccessor so this works with both template-driven
* and reactive forms. Materialize's material_select() replaces the native
* <select> with its own custom dropdown UI, so Angular's built-in select
* accessor can't see selections made through it — this directive acts as
* the accessor instead, translating Materialize's jQuery-based change
* events into FormControl updates.
*
* Usage — reactive forms:
*   <select
*     materialize="material_select"
*     [materializeSelectOptions]="myOptions"
*     [formControl]="myForm.controls['fieldName']"
*   >
*
* Usage — template-driven forms:
*   <select
*     materialize="material_select"
*     [materializeSelectOptions]="myOptions"
*     [(ngModel)]="selectedValue"
*   >
*/
@Directive({
  selector: '[materialize="material_select"]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // forwardRef needed because this provider is declared before the
      // class body is fully defined
      useExisting: forwardRef(() => MaterialSelectDirective),
      multi: true
    }
  ]
})
export class MaterialSelectDirective
  implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  @Input() materializeSelectOptions: any[] = [];

  private isDestroyed = false;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: any) => void = () => { };
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => { };

  constructor(
    private el: ElementRef<HTMLSelectElement>,
    private renderer: Renderer2
  ) { }

  ngAfterViewInit(): void {
    setTimeout(() => this.initSelect(), 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['materializeSelectOptions'] &&
      !changes['materializeSelectOptions'].firstChange
    ) {
      this.destroySelect();
      setTimeout(() => this.initSelect(), 0);
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;

    const $ = this.getJQuery();
    if ($) {
      // Remove only the listener this directive attached
      $(this.el.nativeElement).off('change.cva');
    }

    this.destroySelect();
  }

  // ControlValueAccessor implementation

  writeValue(value: any): void {
    if (this.isDestroyed) return;

    const select = this.el.nativeElement;
    select.value = value ?? '';

    // Re-run material_select so Materialize's custom dropdown UI
    // visually reflects the new value
    const $ = this.getJQuery();
    if ($?.fn?.material_select) {
      this.initializeMaterialSelect($);
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);

    const $ = this.getJQuery();
    if ($?.fn?.material_select) {
      this.initializeMaterialSelect($);
    }
  }

  private initSelect(): void {
    if (this.isDestroyed) return;

    // jQuery not ready
    const $ = this.getJQuery();
    if (!$) {
      console.warn('[MaterialSelectDirective] jQuery not available');
      return;
    }

    // Plugin not available
    if (!$.fn?.material_select) {
      console.warn('[MaterialSelectDirective] material_select plugin not available');
      return;
    }

    this.initializeMaterialSelect($);

    // Materialize announces selection changes through jQuery's own event
    // system (.trigger('change')), which native addEventListener/Renderer2
    // does not receive. Listen via jQuery so the change is actually caught,
    // then push it into the FormControl via onChange/onTouched.
    $(this.el.nativeElement).off('change.cva').on('change.cva', () => {
      const value = this.el.nativeElement.value;
      this.onChange(value);
      this.onTouched();
    });
  }

  private initializeMaterialSelect($: any): void {
    const select = $(this.el.nativeElement);
    select.material_select();

    // Opening on focus must not move an option under the mouse before mouseup.
    // Materialize reads this setting when positioning the dropdown on open.
    const input = select.siblings('input.select-dropdown');
    input.attr('data-beloworigin', 'true');

    // Each rebuild creates a new input. Give it an ID derived from
    // Materialize's unique dropdown ID without duplicating the select's ID.
    const dropdownId = input.attr('data-activates');
    if (dropdownId) {
      input.attr('id', `${dropdownId}-input`);
    }
  }

  private destroySelect(): void {
    const $ = this.getJQuery();
    if (!$?.fn?.material_select) return;

    $(this.el.nativeElement).material_select('destroy');
  }

  private getJQuery(): any {
    return (window as any).$;
  }
}
